import { logEmail } from '../firebase/firestoreService';

/**
 * Invia sollecito di pagamento per una fattura.
 * Funzione riutilizzabile (da UI diretta o da Olli).
 */
export async function sendPaymentReminder({ invoice, invoices, clients, user, userId, triggeredBy = 'ui' }) {
  if (!invoice) {
    return { success: false, error: 'Nessuna fattura specificata.' };
  }
  if (invoice.paid) {
    return { success: false, error: `La fattura ${invoice.invoiceCode} è già incassata.` };
  }

  const client = clients.find(
    (c) => c.name && c.name.toLowerCase() === invoice.clientName.toLowerCase()
  );
  const toEmail = client?.email || client?.pecEmail;
  if (!toEmail) {
    return {
      success: false,
      error: `Nessun indirizzo email trovato per ${invoice.clientName}. Aggiungi un'email o PEC nell'anagrafica cliente.`,
    };
  }

  // Tutte le fatture non pagate dello stesso cliente
  const unpaidInvoices = invoices
    .filter((inv) => !inv.paid && inv.clientName?.toLowerCase() === invoice.clientName.toLowerCase())
    .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));

  const fmtAmount = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('it-IT') : 'non specificata');

  const invoice_list = unpaidInvoices
    .map((inv) => `• Fattura ${inv.invoiceCode} — Scadenza: ${fmtDate(inv.dueDate)} — ${fmtAmount(inv.grossAmount)}`)
    .join('\n');

  const totalAmount = unpaidInvoices.reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);
  const invoice_total = fmtAmount(totalAmount);
  const invoice_code =
    unpaidInvoices.length === 1 ? unpaidInvoices[0].invoiceCode : `${unpaidInvoices.length} fatture`;

  const dueDate = invoice.dueDate || invoice.issueDate;
  const giorni = Math.max(0, Math.floor((new Date() - new Date(dueDate)) / 86400000));

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Forfy';

  const templateParams = {
    to_email: toEmail,
    reply_to: 'noreply@forfy.it',
    from_name: userName,
    client_name: String(invoice.clientName || ''),
    invoice_code: String(invoice_code || ''),
    invoice_amount: invoice_total,
    due_date: invoice.dueDate ? fmtDate(invoice.dueDate) : 'non specificata',
    days_overdue: giorni > 0 ? String(giorni) : '0',
    invoice_list,
    invoice_total,
  };

  console.log('[EmailService] sendPaymentReminder — client match:', { clientId: client?.id, clientName: invoice.clientName, toEmail });

  const emailLogData = {
    clientId: client?.id || null,
    clientName: invoice.clientName,
    type: 'sollecito',
    subject: `Sollecito pagamento – Fattura ${invoice_code}`,
    sentTo: toEmail,
    recipientType: toEmail === client?.pecEmail ? 'pec' : 'email',
    triggeredBy,
    invoiceId: invoice.id || null,
    invoiceCode: invoice.invoiceCode,
    invoices: unpaidInvoices.map((inv) => ({
      id: inv.id,
      invoiceCode: inv.invoiceCode,
      grossAmount: inv.grossAmount,
      dueDate: inv.dueDate || null,
      daysOverdue: Math.max(0, Math.floor((new Date() - new Date(inv.dueDate || inv.issueDate)) / 86400000)),
    })),
    totalAmount: totalAmount,
    invoiceCount: unpaidInvoices.length,
    templateParams: {
      client_name: templateParams.client_name,
      invoice_total: templateParams.invoice_total,
      days_overdue: templateParams.days_overdue,
    },
  };

  try {
    const emailjs = (await import('@emailjs/browser')).default;
    await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_REMINDER_TEMPLATE_ID,
      templateParams,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    );

    console.log('[DEBUG] logEmail check — userId:', userId, 'clientId:', emailLogData.clientId, 'type:', emailLogData.type);
    if (userId) {
      await logEmail(userId, { ...emailLogData, status: 'sent' });
      console.log('[DEBUG] logEmail completed successfully');
    } else {
      console.warn('[DEBUG] logEmail SKIPPED — userId is falsy:', userId);
    }

    const summaryMsg = unpaidInvoices.length === 1
      ? `per la fattura ${unpaidInvoices[0].invoiceCode}`
      : `per ${unpaidInvoices.length} fatture scadute (totale: ${invoice_total})`;
    const invoiceListMsg = unpaidInvoices
      .map((inv) => `  • ${inv.invoiceCode} — ${fmtAmount(inv.grossAmount)}`)
      .join('\n');

    return {
      success: true,
      message: `Sollecito inviato a ${toEmail} ${summaryMsg}.\n\nFatture incluse:\n${invoiceListMsg}`,
      data: { sentTo: toEmail, invoiceCode: invoice.invoiceCode, invoiceCount: unpaidInvoices.length },
    };
  } catch (err) {
    if (userId) {
      await logEmail(userId, { ...emailLogData, status: 'failed', error: err.message || err.text });
    }
    return { success: false, error: `Errore invio sollecito: ${err.message || err.text}` };
  }
}
